import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useProperty } from "@/context/PropertyContext";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, FileSignature, Printer, Scissors, Pencil, History, Save, Eye, Download, HelpCircle } from "lucide-react";
import { eur, fechaCorta } from "@/lib/format";
import { contratoToHtml, CONTRATO_CSS } from "@/lib/contractHtml";
import { toast } from "sonner";

export default function Contracts() {
  const { selectedId, selected } = useProperty();
  const navigate = useNavigate();
  const location = useLocation();
  const [contracts, setContracts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [docHtml, setDocHtml] = useState("");
  const [docContractId, setDocContractId] = useState(null);
  const [docSaving, setDocSaving] = useState(false);
  const [pdfSaving, setPdfSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyDocs, setHistoryDocs] = useState([]);
  const [generatedContractIds, setGeneratedContractIds] = useState(new Set());
  const [historyTitle, setHistoryTitle] = useState("");
  const [form, setForm] = useState(null);
  const [acortar, setAcortar] = useState(null);
  const [nuevaFecha, setNuevaFecha] = useState("");
  const [retenerFianza, setRetenerFianza] = useState(true);
  const [prorrateo, setProrrateo] = useState(null); // vista previa del periodo de salida
  const [prorratear, setProrratear] = useState(false);
  const [importeProrrateo, setImporteProrrateo] = useState("");
  const [updatePagos, setUpdatePagos] = useState(false);
  const [prorateUltimoPago, setProrateUltimoPago] = useState(true);

  const daysInMonth = (y, m) => new Date(y, m, 0).getDate(); // m=1..12
  const isoDateToParts = (yyyyMmDd) => {
    if (!yyyyMmDd) return null;
    const [y, m, d] = String(yyyyMmDd).split("-").map((x) => Number(x));
    if (!y || !m || !d) return null;
    return { y, m, d };
  };

  const calcMesesFromDates = (startYmd, endYmd) => {
    const s = isoDateToParts(startYmd);
    const e = isoDateToParts(endYmd);
    if (!s || !e) return null;
    const monthsDiff = (e.y - s.y) * 12 + (e.m - s.m);
    // If it ends on/before the start day of month, don't count that month as a full one.
    const adjust = e.d <= s.d ? 0 : 1;
    return Math.max(monthsDiff + adjust, 1);
  };

  const calcFechaFinFromMeses = (startYmd, meses) => {
    const s = isoDateToParts(startYmd);
    if (!s) return null;
    const m = Math.max(Number(meses) || 1, 1);
    const start = new Date(Date.UTC(s.y, s.m - 1, s.d));
    const end = new Date(start);
    end.setUTCMonth(end.getUTCMonth() + m);
    // fecha_fin is the last day covered (end date exclusive in JS), so subtract 1 day.
    end.setUTCDate(end.getUTCDate() - 1);
    const yy = end.getUTCFullYear();
    const mm = String(end.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(end.getUTCDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };

  const defaultForm = () => ({
    id: null,
    tenant_id: "", habitacion: "", fecha_inicio: "", meses: 12,
    alquiler: (selected?.habitaciones?.[0]?.precio ?? 430) + (selected?.gasto_mensual_default ?? 60),
    gastos: selected?.gasto_mensual_default ?? 60,
    fianza: selected?.fianza_default ?? 400,
    generar_pagos: true,
    prorroga_indefinida: false,
    renovacion: false,
    estado: "activo",
    fianza_devuelta: false,
  });

  const load = () => {
    if (!selectedId) return setLoading(false);
    setLoading(true);
    Promise.all([
      api.get(`/contracts?property_id=${selectedId}`),
      api.get(`/tenants?property_id=${selectedId}`),
      api.get(`/contract-documents?property_id=${selectedId}`),
    ]).then(([c, t, d]) => {
      setContracts(c.data);
      setTenants(t.data);
      setGeneratedContractIds(new Set((d.data || []).map((doc) => doc.contract_id).filter(Boolean)));
    }).finally(() => setLoading(false));
  };
  useEffect(load, [selectedId]);

  // Viene de "Crear contrato" en la reserva de un inquilino: abrir el alta con él elegido
  const nuevoContratoTenant = location.state?.nuevoContratoTenant;
  useEffect(() => {
    if (!nuevoContratoTenant || loading || !tenants.some((t) => t.id === nuevoContratoTenant)) return;
    setForm(defaultForm());
    setField("tenant_id", nuevoContratoTenant);
    setOpen(true);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nuevoContratoTenant, loading, tenants]);

  const openNew = () => { setForm(defaultForm()); setOpen(true); };
  const openEdit = (c) => {
    setUpdatePagos(false);
    setProrateUltimoPago(true);
    setForm({
      id: c.id,
      original_fecha_fin: (c.fecha_fin || "").slice(0, 10),
      tenant_id: c.tenant_id,
      habitacion: String(c.habitacion || ""),
      fecha_inicio: (c.fecha_inicio || "").slice(0, 10),
      fecha_fin: (c.fecha_fin || "").slice(0, 10),
      fecha_fin_real: (c.fecha_fin_real || "").slice(0, 10),
      meses: c.meses || 12,
      alquiler: c.alquiler || 0,
      gastos: c.gastos || 0,
      fianza: c.fianza || 0,
      generar_pagos: false,
      prorroga_indefinida: !!c.prorroga_indefinida,
      renovacion: !!c.renovacion,
      // Un recortado se guarda como finalizado + recortado: mostrar su estado real.
      estado: c.recortado ? "recortado" : (c.estado || "activo"),
      original_estado: c.recortado ? "recortado" : (c.estado || "activo"),
      recortado: !!c.recortado,
      fianza_devuelta: !!c.fianza_devuelta,
    });
    setOpen(true);
  };
  const setField = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // Bidirectional recalculation between meses and fecha_fin.
      if (k === "meses" && next.fecha_inicio) {
        const fin = calcFechaFinFromMeses(next.fecha_inicio, v);
        if (fin) next.fecha_fin = fin;
      }
      if (k === "fecha_fin" && next.fecha_inicio) {
        const meses = calcMesesFromDates(next.fecha_inicio, v);
        if (meses) next.meses = meses;
      }
      // Contrato nuevo de un inquilino que ya tuvo otro en este piso: es una renovación.
      if (k === "tenant_id" && !f.id) {
        next.renovacion = contracts.some((c) => c.tenant_id === v);
        // Si se le hizo un resguardo de reserva, el contrato parte de esos datos.
        const r = tenants.find((t) => t.id === v)?.reserva;
        if (r && !next.renovacion) {
          if (r.habitacion) next.habitacion = String(r.habitacion);
          if (r.fecha_entrada) next.fecha_inicio = String(r.fecha_entrada).slice(0, 10);
          if (r.meses) next.meses = r.meses;
          if (r.alquiler) next.alquiler = r.alquiler;
          if (r.importe != null) next.fianza = r.importe;
          const fin = next.fecha_inicio && calcFechaFinFromMeses(next.fecha_inicio, next.meses);
          if (fin) next.fecha_fin = fin;
        }
      }
      // Una renovación ya está en prórroga indefinida desde el inicio.
      if ((k === "tenant_id" || k === "renovacion") && next.renovacion) next.prorroga_indefinida = true;
      // Reabrir un contrato cerrado: la duración vuelve a la del periodo inicial (fecha_fin).
      if (k === "estado" && ["finalizado", "recortado"].includes(f.original_estado) && ["activo", "pendiente"].includes(v)) {
        const meses = next.fecha_inicio && next.fecha_fin ? calcMesesFromDates(next.fecha_inicio, next.fecha_fin) : null;
        if (meses) next.meses = meses;
      }
      return next;
    });
  };

  const reabriendo = !!form && ["finalizado", "recortado"].includes(form.original_estado) && ["activo", "pendiente"].includes(form.estado);

  const save = async () => {
    if (!form.tenant_id) return toast.error("Selecciona un inquilino");
    if (!form.fecha_inicio) return toast.error("Indica la fecha de inicio");
    if (Number(form.alquiler) <= 0) return toast.error("Indica un Alquiler total/mes mayor que 0€");
    if (Number(form.gastos) < 0 || Number(form.fianza) < 0) return toast.error("Los importes no pueden ser negativos");

    const recortadoSelected = form.estado === "recortado";
    if (form.estado === "recortado" && form.original_estado !== "recortado") {
      if (!form.fecha_fin) return toast.error("Para marcar Recortado, indica la fecha de fin");
      if (form.original_fecha_fin && form.fecha_fin === form.original_fecha_fin)
        return toast.error("Para marcar Recortado, cambia la fecha de fin");
    }

    const estadoGuardar = form.estado === "recortado" ? "finalizado" : form.estado;
    const base = {
      tenant_id: form.tenant_id,
      habitacion: String(form.habitacion),
      fecha_inicio: new Date(form.fecha_inicio).toISOString(),
      // Only send fecha_fin when user actually changed it (otherwise let backend recompute when meses changes).
      ...(form.fecha_fin && form.fecha_fin !== form.original_fecha_fin
        ? { fecha_fin: new Date(form.fecha_fin).toISOString() }
        : {}),
      ...(form.fecha_fin_real ? { fecha_fin_real: new Date(form.fecha_fin_real).toISOString() } : {}),
      meses: Number(form.meses) || 12,
      alquiler: Number(form.alquiler) || 0,
      gastos: Number(form.gastos) || 0,
      fianza: Number(form.fianza) || 0,
      prorroga_indefinida: !!form.prorroga_indefinida || !!form.renovacion,
      renovacion: !!form.renovacion,
      estado: estadoGuardar,
      recortado: recortadoSelected,
    };
    if (form.id && estadoGuardar === "finalizado") {
      base.fianza_devuelta = !recortadoSelected && !!form.fianza_devuelta;
    }
    try {
      if (form.id) {
        await api.put(`/contracts/${form.id}`, { ...base, update_pagos: updatePagos, prorate_ultimo_pago: prorateUltimoPago });
        toast.success("Contrato actualizado");
      } else {
        await api.post("/contracts", { property_id: selectedId, ...base, generar_pagos: form.generar_pagos });
        toast.success(form.generar_pagos ? "Contrato creado y calendario de pagos generado" : "Contrato creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (form.id ? "Error al actualizar contrato" : "Error al crear contrato"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar contrato y sus pagos asociados?")) return;
    await api.delete(`/contracts/${id}`);
    toast.success("Contrato eliminado");
    load();
  };

  const generarDoc = async (id) => {
    try {
      const { data } = await api.get(`/contracts/${id}/documento`);
      setDocHtml(contratoToHtml(data.documento || "", {
        arrendador: selected?.arrendador_nombre || "",
      }));
      setDocContractId(id);
      setDocOpen(true);
    } catch { toast.error("Error al generar el documento"); }
  };

  const fullDocumentHtml = (html) => `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>Contrato de arrendamiento</title><style>${CONTRATO_CSS}</style></head><body>${html}</body></html>`;

  const signedPdfFilename = () => {
    const contract = contracts.find((c) => c.id === docContractId) || {};
    const tenant = (contract.tenant_nombre || "CONTRATO").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const name = tenant.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "CONTRATO";
    const year = ((contract.fecha_inicio || contract.created_at || new Date().toISOString()).match(/^\d{4}/) || [new Date().getFullYear()])[0];
    return `CONTRATO_ALQUILER_${name}_${year}.pdf`;
  };

  const saveSnapshot = async () => {
    if (!docContractId || !docHtml) return;
    setDocSaving(true);
    try {
      await api.post(`/contracts/${docContractId}/documentos`, { html: fullDocumentHtml(docHtml) });
      setGeneratedContractIds((ids) => new Set([...ids, docContractId]));
      toast.success("Foto del contrato guardada");
    } catch {
      toast.error("Error al guardar la foto del contrato");
    } finally {
      setDocSaving(false);
    }
  };

  const openHistory = async (c) => {
    try {
      const { data } = await api.get(`/contract-documents?contract_id=${c.id}`);
      setHistoryDocs(data || []);
      if ((data || []).length > 0) setGeneratedContractIds((ids) => new Set([...ids, c.id]));
      setHistoryTitle(`${c.tenant_nombre} · Hab. ${c.habitacion}`);
      setHistoryOpen(true);
    } catch {
      toast.error("Error al cargar el histórico");
    }
  };

  const viewSnapshot = async (id) => {
    try {
      const { data } = await api.get(`/contract-documents/${id}`);
      const w = window.open("", "_blank");
      w.document.write(data.html || "");
      w.document.close();
    } catch {
      toast.error("Error al abrir la foto del contrato");
    }
  };

  const deleteSnapshot = async (doc) => {
    if (!window.confirm("¿Eliminar este contrato generado del histórico?")) return;
    try {
      await api.delete(`/contract-documents/${doc.id}`);
      const nextDocs = historyDocs.filter((d) => d.id !== doc.id);
      setHistoryDocs(nextDocs);
      if (!nextDocs.some((d) => d.contract_id === doc.contract_id)) {
        setGeneratedContractIds((ids) => {
          const updated = new Set(ids);
          updated.delete(doc.contract_id);
          return updated;
        });
      }
      toast.success("Contrato generado eliminado");
    } catch {
      toast.error("Error al eliminar el contrato generado");
    }
  };

  const printDoc = () => {
    const w = window.open("", "_blank");
    w.document.write(fullDocumentHtml(docHtml));
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const openSignedPdf = async () => {
    if (!docHtml) return;
    const pdfWindow = window.open("", "_blank");
    if (pdfWindow) {
      pdfWindow.document.write("<p style='font-family: system-ui; padding: 24px;'>Generando PDF firmado...</p>");
      pdfWindow.document.close();
    }
    setPdfSaving(true);
    try {
      const filename = signedPdfFilename();
      const { data } = await api.post(
        "/contracts/documento/pdf-link",
        { html: fullDocumentHtml(docHtml), filename },
      );
      const url = `${API.replace(/\/api$/, "")}${data.url}`;
      if (pdfWindow) {
        pdfWindow.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      toast.success("PDF firmado abierto");
    } catch (e) {
      if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
      toast.error(e?.response?.data?.detail || "Error generando el PDF firmado");
    } finally {
      setPdfSaving(false);
    }
  };

  const openAcortar = (c) => {
    setAcortar(c);
    setNuevaFecha((c.fecha_fin_real || c.fecha_fin || new Date().toISOString()).slice(0, 10));
    setRetenerFianza(true);
  };

  useEffect(() => {
    if (!acortar || !nuevaFecha) { setProrrateo(null); return; }
    let cancelado = false;
    api.get(`/contracts/${acortar.id}/acortar/preview`, { params: { fecha: nuevaFecha } })
      .then(({ data }) => {
        if (cancelado) return;
        setProrrateo(data);
        setImporteProrrateo(String(data.importe_sugerido));
        // Por defecto solo se prorratea si se cumplió la estancia mínima (si no, pierde la fianza
        // y lo habitual es que agote el mes pagado).
        setProrratear(data.aplicable && data.cumple_estancia_minima && data.dias_ocupados < data.dias_periodo && !data.pago_realizado);
      })
      .catch(() => { if (!cancelado) { setProrrateo(null); setProrratear(false); } });
    return () => { cancelado = true; };
  }, [acortar, nuevaFecha]);

  const mesesRecortado = acortar && nuevaFecha
    ? calcMesesFromDates((acortar.fecha_inicio || "").slice(0, 10), nuevaFecha)
    : null;

  const confirmarAcortar = async () => {
    if (!nuevaFecha) return toast.error("Indica la nueva fecha de fin");
    const importe = Number(String(importeProrrateo).replace(",", "."));
    if (prorratear && (!Number.isFinite(importe) || importe < 0)) return toast.error("Importe prorrateado no válido");
    try {
      const { data } = await api.post(`/contracts/${acortar.id}/acortar`, {
        nueva_fecha_fin: new Date(nuevaFecha).toISOString(),
        retener_fianza: retenerFianza,
        importe_ultimo_pago: prorratear ? importe : null,
      });
      const fianzaMsg = data.fianza_retenida
        ? `, fianza ${eur(data.fianza_retenida)} retenida como ingreso`
        : data.fianza_ya_incluida
          ? ", fianza ya contabilizada en los pagos del contrato"
          : "";
      const prorrateoMsg = data.ultimo_pago_prorrateado != null
        ? `, última mensualidad prorrateada a ${eur(data.ultimo_pago_prorrateado)}`
        : "";
      toast.success(`Contrato finalizado. ${data.pagos_eliminados} pago(s) eliminados${prorrateoMsg}${fianzaMsg}.`);
      setAcortar(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al acortar el contrato");
    }
  };

  if (!selectedId)
    return <div className="data-card p-10 text-center text-muted-foreground">Selecciona o crea un piso primero.</div>;

  return (
    <div data-testid="contracts-page">
      <PageHeader title="Contratos" subtitle={`${contracts.length} registrados`}>
        <Button onClick={openNew} data-testid="add-contract-button"><Plus className="h-4 w-4 mr-1.5" /> Nuevo contrato</Button>
      </PageHeader>

      <div className="data-card overflow-hidden">
        <Table data-testid="contracts-table">
          <TableHeader><TableRow>
            <TableHead>Inquilino</TableHead><TableHead>Hab.</TableHead>
            <TableHead>Inicio</TableHead><TableHead className="hidden lg:table-cell">Fin inicial</TableHead>
            <TableHead className="hidden lg:table-cell text-right">Meses</TableHead>
            <TableHead className="hidden lg:table-cell text-right">Alquiler</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="w-36"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Cargando…</TableCell></TableRow>
            ) : contracts.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Sin contratos</TableCell></TableRow>
            ) : (
              contracts.map((c) => (
                <TableRow key={c.id} data-testid={`contract-row-${c.id}`}>
                  <TableCell className="font-medium">
                    <button
                      type="button"
                      onClick={() => navigate(`/inquilinos/${c.tenant_id}`)}
                      className="text-left hover:text-primary hover:underline underline-offset-4 transition-colors"
                      data-testid={`contract-tenant-link-${c.id}`}
                    >
                      {c.tenant_nombre}
                    </button>
                  </TableCell>
                  <TableCell>{c.habitacion}</TableCell>
                  <TableCell className="font-mono text-sm">{fechaCorta(c.fecha_inicio)}</TableCell>
                  <TableCell className="hidden lg:table-cell font-mono text-sm">{fechaCorta(c.fecha_fin)}</TableCell>
                  <TableCell className="hidden lg:table-cell text-right font-mono">{c.meses}</TableCell>
                  <TableCell className="hidden lg:table-cell text-right font-mono">{eur(c.alquiler)}</TableCell>
                  <TableCell>
                    {c?.recortado ? (
                      <Badge className="bg-warning/15 text-warning hover:bg-warning/15 border-0">Recortado</Badge>
                    ) : c.estado === "finalizado" ? (
                      <Badge className="bg-destructive/15 text-destructive hover:bg-destructive/15 border-0">Finalizado</Badge>
                    ) : c.estado === "pendiente" ? (
                      <Badge className="bg-warning/15 text-warning hover:bg-warning/15 border-0">Pendiente</Badge>
                    ) : c.renovacion ? (
                      <Badge className="bg-success/15 text-success hover:bg-success/15 border-0">Activo · Renovación</Badge>
                    ) : c.prorroga_indefinida ? (
                      <Badge className="bg-success/15 text-success hover:bg-success/15 border-0">Activo · Prórroga</Badge>
                    ) : (
                      <Badge className="bg-success/15 text-success hover:bg-success/15 border-0">Activo</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(c)} className="p-1.5 text-muted-foreground hover:text-foreground" title="Editar contrato" data-testid={`edit-${c.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => generarDoc(c.id)} className="p-1.5 text-muted-foreground hover:text-primary" title="Generar contrato" data-testid={`generate-doc-${c.id}`}>
                        <FileSignature className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openHistory(c)}
                        className={`p-1.5 hover:text-primary ${generatedContractIds.has(c.id) ? "text-success" : "text-muted-foreground"}`}
                        title="Histórico de contratos generados"
                        data-testid={`history-doc-${c.id}`}
                      >
                        <History className="h-4 w-4" />
                      </button>
                      <button onClick={() => openAcortar(c)} className="p-1.5 text-muted-foreground hover:text-warning" title="Finalizar contrato" data-testid={`shorten-${c.id}`}>
                        <Scissors className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(c.id)} className="p-1.5 text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create / edit contract */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Editar contrato" : "Nuevo contrato"}</DialogTitle>
            <DialogDescription>
              {form?.id
                ? "Corrige datos del contrato sin crear uno nuevo. Nota: no se regeneran pagos automaticamente al editar."
                : "Crea un contrato y, opcionalmente, genera el calendario de pagos mensual."}
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div>
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setField("estado", v)}>
                  <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="finalizado">Finalizado</SelectItem>
                    <SelectItem value="recortado">Recortado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Inquilino *</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setField("tenant_id", v)}>
                  <SelectTrigger data-testid="contract-tenant-select"><SelectValue placeholder="Selecciona inquilino" /></SelectTrigger>
                  <SelectContent>
                    {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre} {t.apellidos}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Habitación</Label>
                  <Select value={String(form.habitacion)} onValueChange={(v) => setField("habitacion", v)}>
                    <SelectTrigger><SelectValue placeholder="Hab." /></SelectTrigger>
                    <SelectContent>
                      {(selected?.habitaciones || []).map((h) => <SelectItem key={h.numero} value={String(h.numero)}>Hab. {h.numero} · {eur(h.precio)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Fecha inicio *</Label><Input type="date" value={form.fecha_inicio} onChange={(e) => setField("fecha_inicio", e.target.value)} data-testid="contract-fecha-input" /></div>
              </div>
              {form.id && (
                <div>
                  <Label>Fecha fin inicial</Label>
                  <Input type="date" value={form.fecha_fin || ""} onChange={(e) => setField("fecha_fin", e.target.value)} />
                  <p className="text-xs text-muted-foreground mt-1">
                    Se usa en el contrato como fin del periodo inicial. Con prórroga indefinida no finaliza automáticamente.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Meses</Label><Input type="number" value={form.meses} onChange={(e) => setField("meses", e.target.value)} /></div>
                <div>
                  <Label className="inline-flex items-center gap-1">
                    Alquiler total/mes
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label="¿Qué es Alquiler total/mes?"
                          className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center border-0 bg-transparent p-0 leading-none text-muted-foreground hover:text-primary"
                        >
                          <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 text-xs leading-relaxed">
                        Es la <strong>cuota del alquiler + los gastos incluidos</strong> (agua, luz, comunidad, etc.): el importe total que paga el inquilino cada mes. No debe coincidir con la Fianza.
                      </PopoverContent>
                    </Popover>
                  </Label>
                  <Input type="number" min="0" value={form.alquiler} onChange={(e) => setField("alquiler", e.target.value)} />
                  {Number(form.alquiler) > 0 && Number(form.alquiler) === Number(form.fianza) && (
                    <p className="text-xs text-warning mt-1">
                      Coincide con la Fianza. Normalmente la fianza es 1 o 2 mensualidades, no el mismo importe.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Gastos incl.</Label>
                  <Input type="number" min="0" value={form.gastos} onChange={(e) => setField("gastos", e.target.value)} />
                  {Number(form.gastos) === 0 && (
                    <p className="text-xs text-warning mt-1">
                      Gastos a 0€: recuerda que este importe también figura en la plantilla del contrato.
                    </p>
                  )}
                </div>
              </div>
              <div>
                <Label>Fianza</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" value={form.fianza} onChange={(e) => setField("fianza", e.target.value)} />
                  <Button
                    type="button" variant="outline" size="sm" className="shrink-0"
                    title="Fianza = 1 mensualidad (Alquiler total/mes − Gastos incl.)"
                    onClick={() => setField("fianza", (Number(form.alquiler) || 0) - (Number(form.gastos) || 0))}
                  >
                    x1
                  </Button>
                  <Button
                    type="button" variant="outline" size="sm" className="shrink-0"
                    title="Fianza = 2 mensualidades (Alquiler total/mes − Gastos incl.)"
                    onClick={() => setField("fianza", ((Number(form.alquiler) || 0) - (Number(form.gastos) || 0)) * 2)}
                  >
                    x2
                  </Button>
                </div>
                {Number(form.fianza) === 0 && (
                  <p className="text-xs text-warning mt-1">
                    Fianza a 0€: recuerda que este importe también figura en la plantilla del contrato.
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Prórroga indefinida tras el periodo inicial</p>
                  <p className="text-xs text-muted-foreground">El contrato sigue activo tras el fin inicial hasta que se finalice con una salida real.</p>
                </div>
                <Switch
                  checked={!!form.prorroga_indefinida || !!form.renovacion}
                  disabled={!!form.renovacion}
                  onCheckedChange={(v) => setField("prorroga_indefinida", v)}
                />
              </div>

              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Renovación (el inquilino ya cumplió el año inicial)</p>
                  <p className="text-xs text-muted-foreground">
                    Sin nuevo plazo mínimo: queda en prórroga indefinida desde el inicio y el documento usa los bloques [[RENOVACION]] de la plantilla.
                  </p>
                </div>
                <Switch checked={!!form.renovacion} onCheckedChange={(v) => setField("renovacion", v)} data-testid="renovacion-switch" />
              </div>

              {form.id && reabriendo && (
                <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs space-y-1" data-testid="reabrir-aviso">
                  <p className="text-sm font-medium text-foreground">Se reabrirá el contrato</p>
                  <p className="text-muted-foreground">
                    Se borra la fecha de salida real, vuelve a la duración inicial ({form.meses} meses), se regeneran las mensualidades
                    pendientes (incluida la prorrateada, a importe completo) y se elimina la fianza retenida o devuelta.
                    Los cobros ya realizados no se modifican.
                  </p>
                </div>
              )}

              {form.id && ["finalizado", "recortado"].includes(form.estado) && (
                <div>
                  <Label>Fecha salida real</Label>
                  <Input type="date" value={form.fecha_fin_real || ""} onChange={(e) => setField("fecha_fin_real", e.target.value)} />
                </div>
              )}

              {form.id && form.estado === "finalizado" && (
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">Fianza devuelta ({eur(Number(form.fianza) || 0)})</p>
                    <p className="text-xs text-muted-foreground">Registra una salida contable y afecta a balance, pagos e IRPF.</p>
                  </div>
                  <Switch checked={!!form.fianza_devuelta} onCheckedChange={(v) => setField("fianza_devuelta", v)} data-testid="fianza-devuelta-switch" />
                </div>
              )}

              {form.id && !reabriendo && (
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">Actualizar pagos futuros asociados</p>
                    <p className="text-xs text-muted-foreground">Ajusta los pagos pendientes al nuevo nº de meses e importe. Si recortas y hay pagos ya cobrados fuera de plazo, se te avisará.</p>
                  </div>
                  <Switch checked={updatePagos} onCheckedChange={setUpdatePagos} data-testid="update-pagos-switch" />
                </div>
              )}

              {form.id && (updatePagos || reabriendo) && (
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">Prorratear último pago si el fin cae a mitad de mes</p>
                    <p className="text-xs text-muted-foreground">Calcula el último pago según días del mes (si la fecha fin está en mitad de mes).</p>
                  </div>
                  <Switch checked={prorateUltimoPago} onCheckedChange={setProrateUltimoPago} data-testid="prorate-ultimo-pago-switch" />
                </div>
              )}
              {!form.id && (
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">Generar calendario de pagos</p>
                    <p className="text-xs text-muted-foreground">Crea un pago mensual por cada mes del contrato</p>
                  </div>
                  <Switch checked={form.generar_pagos} onCheckedChange={(v) => setField("generar_pagos", v)} data-testid="generar-pagos-switch" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-contract-button">{form?.id ? "Guardar cambios" : "Crear contrato"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finalizar contrato */}
      <Dialog open={!!acortar} onOpenChange={(o) => !o && setAcortar(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Finalizar contrato</DialogTitle>
            <DialogDescription>
              El inquilino deja la habitación. Se eliminarán los pagos futuros no realizados posteriores a la salida y, si lo indicas, se retendrá la fianza como ingreso del año.
            </DialogDescription>
          </DialogHeader>
          {acortar && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <p><strong className="text-foreground">{acortar.tenant_nombre}</strong> · Hab. {acortar.habitacion}</p>
                <p>Inicio: {fechaCorta(acortar.fecha_inicio)} · Fin inicial: {fechaCorta(acortar.fecha_fin)}</p>
              </div>
              <div>
                <Label>Fecha salida real *</Label>
                <Input type="date" value={nuevaFecha} onChange={(e) => setNuevaFecha(e.target.value)} data-testid="acortar-fecha-input" />
              </div>
              <div>
                <Label>Meses resultantes</Label>
                <Input value={mesesRecortado ?? ""} readOnly className="bg-secondary/60" data-testid="acortar-meses-readonly" />
              </div>
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Retener fianza ({eur(acortar.fianza)})</p>
                  <p className="text-xs text-muted-foreground">Se registra como ingreso (balance e IRPF del año)</p>
                </div>
                <Switch checked={retenerFianza} onCheckedChange={setRetenerFianza} data-testid="retener-fianza-switch" />
              </div>
              {prorrateo && prorrateo.aplicable && prorrateo.dias_ocupados < prorrateo.dias_periodo && (
                <div className="rounded-md border border-border p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Prorratear última mensualidad (cuota + gastos)</p>
                      <p className="text-xs text-muted-foreground">
                        Periodo {fechaCorta(prorrateo.periodo_inicio)} – {fechaCorta(prorrateo.periodo_fin)}:
                        {" "}{prorrateo.dias_ocupados} de {prorrateo.dias_periodo} días · mensualidad {eur(prorrateo.importe_completo)}
                      </p>
                    </div>
                    <Switch
                      checked={prorratear}
                      onCheckedChange={setProrratear}
                      disabled={prorrateo.pago_realizado}
                      data-testid="prorratear-salida-switch"
                    />
                  </div>
                  {!prorrateo.cumple_estancia_minima && (
                    <p className="text-xs text-warning">
                      No se ha cumplido la estancia mínima de un año (hasta {fechaCorta(prorrateo.fin_estancia_minima)}).
                    </p>
                  )}
                  {prorrateo.pago_realizado && (
                    <p className="text-xs text-muted-foreground">
                      La mensualidad de este periodo ya está cobrada ({eur(prorrateo.pago_importe)}). Si devuelves la diferencia, regístrala desde Pagos.
                    </p>
                  )}
                  {prorratear && (
                    <div>
                      <Label>Importe a cobrar</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={importeProrrateo}
                        onChange={(e) => setImporteProrrateo(e.target.value)}
                        data-testid="prorrateo-importe-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Sugerido: {eur(prorrateo.importe_sugerido)}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcortar(null)}>Cancelar</Button>
            <Button onClick={confirmarAcortar} data-testid="confirm-acortar-button">Finalizar contrato</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document preview */}
      <Dialog open={docOpen} onOpenChange={setDocOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center justify-between pr-12">
              Documento de contrato
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={saveSnapshot} disabled={docSaving} data-testid="save-contract-snapshot-button"><Save className="h-4 w-4 mr-1.5" /> Guardar foto</Button>
                <Button size="sm" variant="outline" onClick={openSignedPdf} disabled={pdfSaving} data-testid="open-signed-pdf-button"><Download className="h-4 w-4 mr-1.5" /> Ver PDF firmado</Button>
                <Button size="sm" onClick={printDoc} data-testid="print-contract-button"><Printer className="h-4 w-4 mr-1.5" /> Imprimir / PDF</Button>
              </div>
            </DialogTitle>
            <DialogDescription>Vista previa del contrato con formato. Guarda una foto para conservar esta versión aunque cambie la plantilla.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto bg-white rounded-md border border-border p-10" data-testid="contract-document-preview">
            <div className="contract-render" dangerouslySetInnerHTML={{ __html: docHtml }} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract document history */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Histórico de contratos generados</DialogTitle>
            <DialogDescription>{historyTitle}</DialogDescription>
          </DialogHeader>
          <div className="data-card overflow-hidden">
            <Table data-testid="contract-documents-history-table">
              <TableHeader><TableRow>
                <TableHead>Guardado</TableHead><TableHead>Inicio</TableHead><TableHead>Fin</TableHead><TableHead>Hab.</TableHead>
                <TableHead className="text-right">Mensualidad</TableHead><TableHead className="text-right">Fianza</TableHead><TableHead className="w-20"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {historyDocs.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Sin fotos guardadas</TableCell></TableRow>
                ) : historyDocs.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-mono text-sm">{fechaCorta(d.created_at)}</TableCell>
                    <TableCell className="font-mono text-sm">{fechaCorta(d.fecha_inicio)}</TableCell>
                    <TableCell className="font-mono text-sm">{fechaCorta(d.fecha_fin)}</TableCell>
                    <TableCell>{d.habitacion}</TableCell>
                    <TableCell className="text-right font-mono">{eur(d.mensualidad)}</TableCell>
                    <TableCell className="text-right font-mono">{eur(d.fianza)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => viewSnapshot(d.id)} className="p-1.5 text-muted-foreground hover:text-primary" title="Ver foto"><Eye className="h-4 w-4" /></button>
                        <button onClick={() => deleteSnapshot(d)} className="p-1.5 text-muted-foreground hover:text-destructive" title="Eliminar contrato generado"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
